import type { MailListDensity } from '../../api/users';

/**
 * 메일 화면 보기 설정 (2026-08-21) — 열림 방식 · 묶어 보기 · 밀도.
 *
 * ★ 기기마다 따로 기억한다(localStorage). 서버에 개인 메일 설정(migration 110)이 있지만
 *   칸이 없어서, 서버 변경 없이 넣을 수 있는 자리에 먼저 둔다. 서버 칸이 생기면
 *   여기 load/save 만 바꿔 끼우면 된다 — 소비처는 그대로다.
 *
 * 값이 깨져 있어도(옛 값·손댄 값) **지금까지와 같은 화면**으로 떨어진다(side / none).
 */

/** 메일을 어떻게 열까. full=창 전체 · side=좌우 · bottom=아래에서 위로. */
export type MailOpenMode = 'full' | 'side' | 'bottom';
/** 목록을 어떻게 묶어 볼까. */
export type MailGroupBy = 'none' | 'date' | 'sender';

export interface MailViewPrefs {
  openMode: MailOpenMode;
  groupBy: MailGroupBy;
  /**
   * 좌우열림에서 **목록이 갖는 몫**(0~1). 나머지가 상세다.
   * 처음 값이 0.34(404px 고정)였는데 상세가 화면의 반을 훌쩍 넘어 목록이 답답했다.
   */
  sideRatio: number;
  /** 상하열림에서 **목록이 갖는 몫**(0~1). 나머지가 상세다. */
  bottomRatio: number;
  /** 한 쪽에 몇 통을 보일까(12·25·50·100). 화면 높이의 문제라 메일함마다 따로 두지 않는다. */
  pageSize: number;
  /**
   * 목록 밀도 (2026-09-05) — 「환경설정 > 기본환경 > 메일 목록 밀도」와 **같은 값**이다.
   *
   *   · NARROW(기본) — 압축형. 아바타·본문 미리보기를 보이지 않는다.
   *   · WIDE          — 아바타·본문 미리보기를 함께 보인다.
   *
   * ★ 여태 이 자리에는 `preview`(본문 미리보기) 하나만 있었다. 밀도는 그것 **하나로 갈리는
   *   것이 아니라** 아바타까지 함께 갈리므로, 값 하나가 둘을 다 정하게 모았다.
   *   옛 값(`preview:true`)은 WIDE 로 읽어 준다 — 켜 두고 쓰던 사람이 좁아지지 않는다.
   * ★ 서버(`mailListDensity`)가 진실이고 여기는 **사본**이다. 목록이 뜨자마자 필요한 값이라
   *   서버 응답을 기다리면 좁게 그렸다가 넓어지는 것이 눈에 보인다.
   */
  density: MailListDensity;
}

export const MAIL_OPEN_MODES: MailOpenMode[] = ['full', 'side', 'bottom'];
export const MAIL_GROUP_BYS: MailGroupBy[] = ['none', 'date', 'sender'];

/** 비율은 이 사이에서만 논다 — 어느 쪽도 쓸 수 없을 만큼 좁아지지 않게. */
export const MAIL_RATIO_MIN = 0.25;
export const MAIL_RATIO_MAX = 0.75;

/** 기본값 = 목록 왼쪽·메일 오른쪽, 묶지 않음, 좌우는 반반에 가깝게. */
export const MAIL_PAGE_SIZE_OPTIONS = [12, 25, 50, 100];

export const DEFAULT_MAIL_VIEW: MailViewPrefs = {
  openMode: 'side',
  groupBy: 'none',
  sideRatio: 0.48,
  bottomRatio: 0.44,
  pageSize: 25,
  density: 'NARROW',
};

export function clampMailRatio(v: number): number {
  if (!Number.isFinite(v)) return DEFAULT_MAIL_VIEW.sideRatio;
  return Math.min(MAIL_RATIO_MAX, Math.max(MAIL_RATIO_MIN, v));
}

export const MAIL_VIEW_STORAGE_KEY = 'axi.mail.view.v1';

function isOpenMode(v: unknown): v is MailOpenMode {
  return typeof v === 'string' && (MAIL_OPEN_MODES as string[]).includes(v);
}
function isGroupBy(v: unknown): v is MailGroupBy {
  return typeof v === 'string' && (MAIL_GROUP_BYS as string[]).includes(v);
}

export function loadMailView(): MailViewPrefs {
  try {
    const raw = localStorage.getItem(MAIL_VIEW_STORAGE_KEY);
    if (!raw) return DEFAULT_MAIL_VIEW;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return DEFAULT_MAIL_VIEW;
    const o = parsed as Record<string, unknown>;
    return {
      openMode: isOpenMode(o.openMode) ? o.openMode : DEFAULT_MAIL_VIEW.openMode,
      groupBy: isGroupBy(o.groupBy) ? o.groupBy : DEFAULT_MAIL_VIEW.groupBy,
      // 값이 없거나 말이 안 되면 기본 비율로 — 화면이 안 뜨는 일이 없게.
      sideRatio: typeof o.sideRatio === 'number' && Number.isFinite(o.sideRatio)
        ? clampMailRatio(o.sideRatio) : DEFAULT_MAIL_VIEW.sideRatio,
      bottomRatio: typeof o.bottomRatio === 'number' && Number.isFinite(o.bottomRatio)
        ? clampMailRatio(o.bottomRatio) : DEFAULT_MAIL_VIEW.bottomRatio,
      // 목록에 없는 크기는 기본값으로 — 임의의 수가 들어오면 쪽 계산이 이상해진다.
      pageSize: MAIL_PAGE_SIZE_OPTIONS.includes(o.pageSize as number)
        ? (o.pageSize as number) : DEFAULT_MAIL_VIEW.pageSize,
      /* 밀도 — 없으면 옛 값(`preview`)을 읽어 준다. 그것도 없으면 기본값(NARROW).
         ★ 옛 값을 안 읽으면 미리보기를 켜 두고 쓰던 사람이 어느 날 갑자기 좁아진다. */
      density: o.density === 'WIDE' || o.density === 'NARROW'
        ? (o.density as MailListDensity)
        : (o.preview === true ? 'WIDE' : DEFAULT_MAIL_VIEW.density),
    };
  } catch {
    return DEFAULT_MAIL_VIEW;
  }
}

export function saveMailView(value: MailViewPrefs): void {
  try {
    localStorage.setItem(MAIL_VIEW_STORAGE_KEY, JSON.stringify(value));
  } catch {
    /* 기기 저장 실패는 무시 — 이번 세션 동안만 유지된다 */
  }
}

/** 밀도가 「넓게」인가 — 아바타·본문 미리보기를 함께 보이는 상태. */
export function isWideDensity(v: { density: MailListDensity }): boolean {
  return v.density === 'WIDE';
}
